import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Settings2, Search, X, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
// THE EMBED CAN CARRY TWO ROWS AND ONLY ONE OF THEM IS THE DRIVER'S. `profiles.driver_id` is unique
// only WHERE `admin_id IS NULL`, so reading it by position could hand back an admin's test profile —
// the name this list is searched by, and the name it writes onto the page's filter badge.
import { filterContractDriverName } from '@/components/contracts/repaymentsDriver';

export interface ContractFilter {
    contract_id: number;
    driver_name: string;
}

interface ContractFilterSheetProps {
    selectedFilter: ContractFilter | null;
    onFilterChange: (filter: ContractFilter | null) => void;
}

const PAGE_SIZE = 10;

type ContractStatus = 'IN_PROGRESS' | 'COMPLETED' | 'TERMINATED';

const getStatusPill = (status: ContractStatus | null) => {
    const baseClasses = "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium";
    switch (status) {
        case 'IN_PROGRESS':
            return <span className={`${baseClasses} bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200`}>In Progress</span>;
        case 'COMPLETED':
            return <span className={`${baseClasses} bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300`}>Completed</span>;
        case 'TERMINATED':
            return <span className={`${baseClasses} bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300`}>Terminated</span>;
        default:
            return <span className={`${baseClasses} bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400`}>Unknown</span>;
    }
};

export function ContractFilterSheet({ selectedFilter, onFilterChange }: ContractFilterSheetProps) {
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const debouncedSearch = useDebounce(searchTerm, 300);

    // Reset page when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch]);

    // Fetch ALL contracts with drivers and their latest status
    const { data: allContracts, isLoading } = useQuery({
        queryKey: ['contracts-for-filter-all'],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('contracts')
                .select(`
                    id,
                    driver_id,
                    drivers(id, profiles(id, admin_id, first_name, last_name)),
                    contract_updates(status, created_at)
                `)
                .order('id', { ascending: false });

            if (error) throw error;

            // Process to get latest status for each contract
            return data?.map(contract => {
                const updates = contract.contract_updates as { status: ContractStatus; created_at: string }[] | null;
                const latestUpdate = updates?.sort((a, b) =>
                    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                )[0];

                return {
                    ...contract,
                    latestStatus: latestUpdate?.status || null
                };
            }) || [];
        },
        enabled: open,
    });

    // Filter contracts by search term
    const filteredContracts = useMemo(() => {
        if (!allContracts) return [];
        if (!debouncedSearch) return allContracts;

        const searchLower = debouncedSearch.toLowerCase();
        const searchNum = parseInt(debouncedSearch);

        return allContracts.filter(contract => {
            // Match by contract ID
            if (!isNaN(searchNum) && contract.id === searchNum) return true;
            // Match by driver name
            const driverName = filterContractDriverName(contract).toLowerCase();
            return driverName.includes(searchLower);
        });
    }, [allContracts, debouncedSearch]);

    const totalCount = filteredContracts.length;
    const totalPages = Math.ceil(totalCount / PAGE_SIZE);
    const from = (currentPage - 1) * PAGE_SIZE;
    const to = from + PAGE_SIZE;

    // Get current page of filtered contracts
    const paginatedContracts = filteredContracts.slice(from, to);

    const handleSelect = (contract: NonNullable<typeof allContracts>[number]) => {
        const driverName = filterContractDriverName(contract);
        onFilterChange({ contract_id: contract.id, driver_name: driverName });
        setOpen(false);
    };

    const handleClear = () => {
        onFilterChange(null);
        setOpen(false);
    };

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="relative">
                    <Settings2 className="h-4 w-4" />
                    {selectedFilter && (
                        <span className="absolute -top-1 -right-1 h-3 w-3 bg-primary rounded-full" />
                    )}
                </Button>
            </SheetTrigger>
            <SheetContent
                side="right"
                hideOverlay
                className="w-[500px] m-4 h-[calc(100vh-32px)] rounded-xl border"
            >
                <SheetHeader>
                    <SheetTitle>Filter by Driver</SheetTitle>
                </SheetHeader>

                <div className="mt-6 flex flex-col h-[calc(100%-60px)]">
                    {/* Selected filter indicator */}
                    {selectedFilter && (
                        <div className="flex items-center justify-between p-3 bg-muted rounded-lg mb-4">
                            <div>
                                <span className="text-sm font-medium">{selectedFilter.driver_name}</span>
                                <p className="text-xs text-muted-foreground">Contract #{selectedFilter.contract_id}</p>
                            </div>
                            <Button variant="ghost" size="sm" onClick={handleClear}>
                                <X className="h-4 w-4" />
                            </Button>
                        </div>
                    )}

                    {/* Search input */}
                    <div className="relative mb-4">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Search by driver name..."
                            className="pl-9"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {/* Pagination info */}
                    {totalCount > 0 && (
                        <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
                            <span>
                                Showing {from + 1}-{Math.min(to, totalCount)} of {totalCount}
                            </span>
                            {totalPages > 1 && (
                                <div className="flex items-center gap-1">
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        className="h-8 w-8"
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                    >
                                        <ChevronLeft className="h-4 w-4" />
                                    </Button>
                                    <span className="px-2">
                                        {currentPage} / {totalPages}
                                    </span>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        className="h-8 w-8"
                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                    >
                                        <ChevronRight className="h-4 w-4" />
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Contract list */}
                    <ScrollArea className="flex-1">
                        <div className="space-y-1">
                            {isLoading ? (
                                <p className="text-center text-muted-foreground py-8">Loading...</p>
                            ) : paginatedContracts.length > 0 ? (
                                paginatedContracts.map((contract) => {
                                    const driverName = filterContractDriverName(contract);
                                    const isSelected = selectedFilter?.contract_id === contract.id;

                                    return (
                                        <button
                                            key={contract.id}
                                            onClick={() => handleSelect(contract)}
                                            className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-colors hover:bg-muted ${isSelected ? 'bg-muted border border-primary' : ''
                                                }`}
                                        >
                                            <div>
                                                <span className="font-medium">{driverName}</span>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <span className="text-sm text-muted-foreground">Contract #{contract.id}</span>
                                                    {getStatusPill(contract.latestStatus)}
                                                </div>
                                            </div>
                                            {isSelected && (
                                                <Check className="h-4 w-4 text-primary" />
                                            )}
                                        </button>
                                    );
                                })
                            ) : (
                                <p className="text-center text-muted-foreground py-8">
                                    {searchTerm ? 'No contracts found' : 'No contracts available'}
                                </p>
                            )}
                        </div>
                    </ScrollArea>
                </div>
            </SheetContent>
        </Sheet>
    );
}
